import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Table, Space, Typography, Popconfirm, message, Modal, DatePicker, TableProps } from 'antd';
import { CopyOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { t } from 'i18next';
import { useStore } from '../../stores/root-store-context';
import dayjs, { Dayjs } from 'dayjs';
import { DateSort, TextSort } from '../../helpers/SorterHelper';

const { Text } = Typography;

const AnonymusAccess = observer(() => {
  const {projectsStore, employeesStore } = useStore();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs().add(7, 'day'));
  const [authors, setAuthors] = useState<{ [key: string]: { firstName: string; lastName: string } }>({});

  const getAccess = async () => {
    const result = await projectsStore.getAccesses();
    if (!result) {
      message.error('Failed to fetch accesses');
      return;
    }

    const uniqueAuthorIds = [...new Set(projectsStore.publicAccess.map((access) => access.authorId))];
    const authorPromises = uniqueAuthorIds.map(async (id) => {
      try {
        const user = await employeesStore.ApiStore.EmployeesApiClient.getUserById(id);
        return { id, firstName: user.firstName, lastName: user.lastName };
      } catch (error) {
        console.error(`Error fetching user ${id}:`, error);
        return { id, firstName: 'Unknown', lastName: '' };
      }
    });

    const authorResults = await Promise.all(authorPromises);
    const authorsMap = authorResults.reduce((acc, { id, firstName, lastName }) => {
      acc[id] = { firstName, lastName };
      return acc;
    }, {} as { [key: string]: { firstName: string; lastName: string } });

    setAuthors(authorsMap);
  };

  useEffect(() => {
    getAccess();
  }, []);

  const showModal = () => setIsModalVisible(true);
  const handleCancel = () => setIsModalVisible(false);

  const handleOk = async () => {
    if (selectedDate) {
      await projectsStore.addAccess(selectedDate);
      getAccess();
      setIsModalVisible(false);
    } else {
      message.warning('Please select a date.');
    }
  };

  const columns: TableProps<any>['columns'] = [
    {
      title: 'Access Link',
      dataIndex: 'jwtToken',
      key: 'accessLink',
      render: (token: string) => {
        const fullLink = `${window.location.origin}/anonymous/project?token=${token}`;
        return <Text code copyable={{ text: fullLink }}>{fullLink}</Text>;
      },
      sorter: (a, b) => TextSort(a.jwtToken, b.jwtToken),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text: string) => dayjs(text).format('DD.MM.YYYY HH:mm'),
      sorter: (a, b) => DateSort(a.createdAt, b.createdAt),
    },
    {
      title: 'Expires',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (text: string) => dayjs(text).format('DD.MM.YYYY HH:mm'),
      sorter: (a, b) => DateSort(a.endDate, b.endDate),
    },
    {
      title: 'Author',
      dataIndex: 'authorId',
      key: 'authorId',
      render: (authorId: string) => {
        const author = authors[authorId] || { firstName: 'Loading', lastName: '' };
        return `${author.firstName} ${author.lastName}`.trim();
      },
      sorter: (a, b) => TextSort(
        `${authors[a.authorId]?.firstName || ''} ${authors[a.authorId]?.lastName || ''}`,
        `${authors[b.authorId]?.firstName || ''} ${authors[b.authorId]?.lastName || ''}`
      ),
    },
    {
      title: 'Actions',
      key: 'operation',
      render: (_, record) => (
        <Space size="middle">
          <Popconfirm
            title="Delete this link?"
            okText="Yes"
            cancelText="No"
            onConfirm={async () => {
              projectsStore.setLoading(true);
              await projectsStore.removeAccess(record.id).catch(error =>
                message.error(`Error: ${error.message}`)
              );
              await getAccess();
              projectsStore.setLoading(false);
            }}
          >
            <Button type="primary" danger shape="circle" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ margin: '20px 0' }}>
      <Space style={{ marginBottom: 20 }}>
        <Button type="primary" style={{ borderRadius: 0 }} onClick={showModal} icon={<PlusOutlined />}>
          {t("Add access")}
        </Button>
      </Space>
      <Table
        columns={columns}
        dataSource={projectsStore.publicAccess.map((el) => ({ ...el, key: el.id }))}
        rowKey="id"
        pagination={false}
      />
      <Modal
        title={t("SelectAccessExpirationDate")}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        okText={t("Add")}
        cancelText={t("Cancel")}
      >
        <DatePicker
          style={{ width: '100%' }}
          value={selectedDate}
          onChange={(date) => setSelectedDate(date)}
          disabledDate={(current) => current && current < dayjs().startOf('day')}
        />
      </Modal>
    </div>
  );
});

export default AnonymusAccess;
